'use client';

import { useTranslations } from 'next-intl';
import { CustomImage as Image } from 'components/shared';
import { CarouselButton } from 'components/shared/CarouselButton';
import { useEffect, useCallback, useState, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './JadeAISection.module.css';

const UNICORN_EMBED_URL = 'https://unicorn.studio/embed/A2jAmfx7qxaHPQvXhvBu';

interface HighlightSlide {
  type: 'image' | 'video';
  key: string;
  desktopImage: string;
  mobileImage: string;
}

const HIGHLIGHTS: HighlightSlide[] = [
  {
    type: 'video',
    key: 'healthConversation',
    desktopImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/01-vid.mp4/w_1080',
    mobileImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/01-vid.mp4/w_1080',
  },
  {
    type: 'image',
    key: 'builtTogether',
    desktopImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/02.png',
    mobileImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/02.png',
  },
  {
    type: 'image',
    key: 'twoModes',
    desktopImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/03.png',
    mobileImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/03.png',
  },
  {
    type: 'image',
    key: 'privacySecurity',
    desktopImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/04.png',
    mobileImage: 'web_v2/ring-pro/landing-page/jade-ai/v2/04.png',
  },
];

const AUTOPLAY_ACTIVE = false;
const AUTOPLAY_INTERVAL = 6000;

export default function JadeAISection({ className }: { className?: string }) {
  const t = useTranslations('ring-pro');
  const sectionRef = useRef<HTMLElement>(null);
  const [showWebGL, setShowWebGL] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const autoplayPlugin = useRef(
    Autoplay({
      delay: AUTOPLAY_INTERVAL,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  const MOBILE_BREAKPOINT = 768;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: false, align: 'center', slidesToScroll: 1 },
    AUTOPLAY_ACTIVE ? [autoplayPlugin.current] : [],
  );

  const [activeSlide, setActiveSlide] = useState(0);

  const [snapCount, setSnapCount] = useState(HIGHLIGHTS.length);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const updateSnapCount = useCallback(() => {
    if (!emblaApi) return;
    setSnapCount(emblaApi.scrollSnapList().length);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    updateSnapCount();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', updateSnapCount);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', updateSnapCount);
    };
  }, [emblaApi, onSelect, updateSnapCount]);

  useEffect(() => {
    if (!emblaApi) return;
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      emblaApi.reInit({ loop: mobile, align: 'center', slidesToScroll: 1 });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [emblaApi]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || !emblaApi) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowWebGL(true);
          if (AUTOPLAY_ACTIVE) autoplayPlugin.current.play();
        } else {
          if (AUTOPLAY_ACTIVE) autoplayPlugin.current.stop();
        }
      },
      { rootMargin: '200px 0px', threshold: 0 },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  return (
    <section ref={sectionRef} className={[styles.section, className].filter(Boolean).join(' ')}>
      {showWebGL && (
        <div className={styles.webGLBackground} aria-hidden="true">
          <iframe
            title="Unicorn Studio"
            src={UNICORN_EMBED_URL}
            loading="lazy"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      )}
      <div className={styles.sectionContent}>
        <p className={styles.caption}>{t('ring-pro.jadeAI.caption')}</p>
        <h2 className={styles.sectionTitle}>{t('ring-pro.jadeAI.title')}</h2>
        <p className={styles.sectionDesc}>
          {t('ring-pro.jadeAI.description')}
        </p>
        <div className={styles.carouselWrapper}>
          <div className={styles.navButtonWrapper} data-side="left">
            <CarouselButton
              direction="prev"
              variant="dark"
              rounded="full"
              fill
              onClick={scrollPrev}
              aria-label={t('ring-pro.common.prevSlide')}
            />
          </div>
          <div className={styles.emblaViewport} ref={emblaRef}>
            <div className={styles.emblaContainer}>
              {HIGHLIGHTS.map((slide, index) => (
                <div className={styles.emblaSlide} key={index} data-active={activeSlide === index ? 'true' : undefined}>
                  <div className={styles.card}>
                    <div className={styles.cardImageWrapper}>
                      {slide.type === 'video' ? (
                        <video
                          className={styles.cardVideo}
                          key={`${index}-${isMobile}`}
                          ref={(el) => {
                            if (el) el.playbackRate = 0.50;
                          }}
                          src={getCompressedAssetUrl(
                            `/${isMobile ? slide.mobileImage : slide.desktopImage}`,
                          )}
                          loop
                          autoPlay
                          muted
                          playsInline
                          preload="none"
                          aria-label={t(`ring-pro.jadeAI.slides.${slide.key}.imageAlt`)}
                        />
                      ) : (
                        <Image
                          src={isMobile ? slide.mobileImage : slide.desktopImage}
                          alt={t(`ring-pro.jadeAI.slides.${slide.key}.imageAlt`)}
                          fill
                          style={{ objectFit: 'cover' }}
                          loading={index === 0 ? 'eager' : 'lazy'}
                        />
                      )}
                    </div>
                  </div>
                  <div className={styles.cardTextBlock} data-active={activeSlide === index ? 'true' : undefined}>
                    <p className={styles.cardHeading}>{t(`ring-pro.jadeAI.slides.${slide.key}.heading`)}</p>
                    <p className={styles.cardBodyText}>{t(`ring-pro.jadeAI.slides.${slide.key}.body`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.navButtonWrapper} data-side="right">
            <CarouselButton
              direction="next"
              variant="dark"
              rounded="full"
              fill
              onClick={scrollNext}
              aria-label={t('ring-pro.common.nextSlide')}
            />
          </div>
        </div>

        <div className={styles.controls}>
          {Array.from({ length: snapCount }, (_, index) => (
            <button
              className={styles.dot}
              key={index}
              data-active={activeSlide === index ? 'true' : undefined}
              onClick={() => scrollTo(index)}
              aria-label={t('ring-pro.common.goToSlide', { index: index + 1 })}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
